import { z } from "zod";

import { AgentIpcEnvelopeBaseSchema } from "../../envelope/envelope-base.schema";
import { AGENT_IPC_KIND } from "../kinds.constant";

/**
 * How the runner process booted, on the SANDBOX's clock only — no field here is
 * ever compared with a host-clock instant.
 *
 * Three deltas, in ms from runner-entry: the first statement of the runner's own
 * entry module. Runner-entry is NOT the process's first executed line. The runner
 * ships as one ESM bundle, so by the time its entry module runs, every static
 * import (the externalised Claude Agent SDK included) and every bundled sibling
 * module (`@nexus/types` and its zod schemas included) has already loaded and
 * evaluated.
 *
 *   - `sdkImportedMs`     — runner-entry → the statement after the SDK import.
 *                           ≈0 by construction for the reason above; it carries
 *                           no SDK cost. `sdkEvaluatedEpochMs` and
 *                           `moduleEvalStartEpochMs` do.
 *   - `queryCalledMs`     — runner-entry → query() invoked. After this the SDK
 *                           spawns the Claude Code binary. Absent when the
 *                           runner never marked query(), rather than a 0.
 *   - `firstSdkMessageMs` — runner-entry → first SDK message received.
 *
 * Five absolute instants, epoch ms on the sandbox's wall clock, which place the
 * process's boot BEFORE runner-entry. Each is optional: a runner built before
 * the field existed omits it, and so does a launch whose shell could not supply
 * one.
 *
 *   - `launchShellEpochMs`     — the codeuser shell, immediately before it
 *                                `exec`s the runner command (`date +%s%3N`,
 *                                whole ms). On the `npx tsx` path the command
 *                                is npx, so npx and tsx's own CLI process sit
 *                                between this and `processStartEpochMs`.
 *   - `processStartEpochMs`    — `performance.timeOrigin` of the process the
 *                                runner code runs in.
 *   - `moduleEvalStartEpochMs` — the whole module graph is loaded, transformed
 *                                (tsx), parsed and linked, and the first module
 *                                begins evaluating — before the SDK evaluates.
 *   - `sdkEvaluatedEpochMs`    — every static import has evaluated. The SDK is
 *                                the only one that is not a node builtin, so
 *                                this minus `moduleEvalStartEpochMs` is the
 *                                SDK's evaluation. Its LOAD and PARSE are not
 *                                separable from the rest of the graph's and sit
 *                                in `moduleEvalStartEpochMs − processStartEpochMs`.
 *   - `runnerEntryEpochMs`     — runner-entry, the zero of the three deltas,
 *                                read on the high-resolution clock.
 *
 * The two module marks are stamped by the bundle's banner (`agent-runner-esbuild.ts`),
 * because only a banner import can run ahead of the SDK's evaluation. A runner
 * loaded as source rather than as a bundle reports neither.
 *
 * `deriveRunnerBootPhases` turns these into the phases a reader queries.
 */
export const RunnerBootTimingSchema = z.object({
  sdkImportedMs: z.number().nonnegative(),
  queryCalledMs: z.number().nonnegative().optional(),
  firstSdkMessageMs: z.number().nonnegative(),
  launchShellEpochMs: z.number().nonnegative().optional(),
  processStartEpochMs: z.number().nonnegative().optional(),
  moduleEvalStartEpochMs: z.number().nonnegative().optional(),
  sdkEvaluatedEpochMs: z.number().nonnegative().optional(),
  runnerEntryEpochMs: z.number().nonnegative().optional()
});

export type RunnerBootTiming = z.infer<typeof RunnerBootTimingSchema>;

/**
 * The SDK's own latency figures for a turn, read off `SDKResultSuccess` and
 * carried verbatim. They measure a different span from `boot`, which is why
 * both are kept: `boot` times the runner process from its own first executed
 * line, these time the model request the SDK made inside it.
 *
 *   - `ttftMs`                    — SDK `ttft_ms`: time to first token
 *   - `ttftStreamMs`              — SDK `ttft_stream_ms`: time to the first
 *                                   token on the streaming channel
 *   - `timeToRequestMs`           — SDK `time_to_request_ms`: the SDK's own
 *                                   pre-request work before the API call left
 *   - `timeToRequestFromSpawnMs`  — SDK `time_to_request_from_spawn_ms`: the
 *                                   same, measured from CLI process spawn
 *   - `warmSpareClaimed`          — SDK `warm_spare_claimed`: the turn ran on a
 *                                   pre-spawned CLI worker. No boot mark can
 *                                   answer this, which is why it is carried
 *   - `timeOriginMs`              — SDK `time_origin_ms`: the absolute clock
 *                                   base the SDK measured its own offsets from
 *   - `requestSentWallMs`         — SDK `request_sent_wall_ms`: the wall-clock
 *                                   instant the SDK sent its request. The one
 *                                   absolute anchor a reader can line up
 *                                   against a timeline outside this system — a
 *                                   provider-side log, an incident window. The
 *                                   vendor publishes a presence condition none
 *                                   of the others carry: the success arm only,
 *                                   and only when `is_error` is false — so an
 *                                   API-error turn omits it by construction
 *
 * Every field is optional because every SDK field is. Four of the seven have
 * never been observed on this transport: across the captured runs behind this
 * cluster (SDK 0.3.210 → 0.3.247) `ttft_ms`, `ttft_stream_ms` and
 * `time_to_request_ms` appear 1245 / 1172 / 1172 times, while
 * `time_to_request_from_spawn_ms`, `warm_spare_claimed`, `time_origin_ms` and
 * `request_sent_wall_ms` appear zero times.
 *
 * Those zeros are measured negatives rather than unrun probes, and the
 * condition that makes them evidence is that the positives sit underneath
 * them: the same result frames, in the same captures, carry the other three at
 * four-digit counts apiece, so the path was exercised. The mechanism agrees for
 * one of them — a warm spare is a CLI process pre-spawn optimisation, and this
 * runner opens `query()` in a fresh sandbox per launch, so nothing here claims
 * one.
 *
 * **The condition each zero rests on is the field being declared while the
 * capture ran, and it is not the same for all four.**
 * `time_to_request_from_spawn_ms`, `warm_spare_claimed` and `time_origin_ms`
 * are declared on `SDKResultSuccess` at 0.3.210 and throughout the range above,
 * so their counts are measured over builds that could have reported them.
 * `request_sent_wall_ms` arrives at 0.3.216, so the corpus-wide figure for it
 * spans builds whose type had no such field — a zero that establishes nothing
 * on its own. Its evidence is the subset captured at or above that threshold:
 * across three runs on 0.3.222 and 0.3.247, 155 result frames carry `ttft_ms`
 * and none carries this one, nor `user_message_uuid`, the field the vendor
 * documents as arriving with it. Read the counts for the other six as the
 * transport's answer over the whole corpus and this one as those three runs.
 *
 * The SDK pinned for the runner and baked into its snapshot is 0.3.293, which
 * declares the field, so a build shipping today would carry the value the day
 * the CLI starts reporting it.
 *
 * They are declared anyway: the cost is an optional field, and the alternative
 * is dropping a value the day the CLI starts sending it. Read an absent one as
 * "the SDK did not report it", never as a broken carry.
 *
 * **This group stops at the wire, and does so deliberately.** Nothing folds it
 * into the chat kernel — no `ToolResult` field, no ordered part — so a reopened
 * conversation shows none of it, and that is correct rather than unfinished:
 * these are host observability figures for one launch, read at session transport
 * finalisation and written to that stream's trace row, which is the durable
 * record for them. A transcript has no place to put a time-to-first-token, and
 * materialising one would put a diagnostic in front of a reader who did not ask
 * for it. The trace row is queryable long after the conversation is closed;
 * what is NOT durable is any browser-side view, because there is none.
 *
 * The seven optional members carried here are the request-latency figures the
 * pinned SDK's success result declares. It declares more timing members beside
 * them — `first_content_frame_ms` and the `first_stream_post_*` /
 * `first_text_post_*` members, ten at 0.3.293 and eight at 0.3.282, none
 * documented on the type — and those are not carried. The set is not closed:
 * the vendor adds members, nothing keys this docblock to a version bump, and
 * the runner copies these seven by name, so a member added later is dropped
 * rather than refused. The two mandatory durations on the same result stay
 * out: they measure the turn, not the request inside it.
 *
 * `requestSentWallMs` is not reconstructed from the members beside it. Adding
 * `timeToRequestMs` to `timeOriginMs` would produce a plausible instant, and it
 * assumes the SDK's own clock base and this wall-clock reading were taken
 * against one clock, which nothing here establishes. The SDK reports it or the
 * member is absent.
 */
export const SdkTurnTimingSchema = z.object({
  ttftMs: z.number().nonnegative().optional(),
  ttftStreamMs: z.number().nonnegative().optional(),
  timeToRequestMs: z.number().nonnegative().optional(),
  timeToRequestFromSpawnMs: z.number().nonnegative().optional(),
  warmSpareClaimed: z.boolean().optional(),
  timeOriginMs: z.number().nonnegative().optional(),
  requestSentWallMs: z.number().nonnegative().optional()
});

export type SdkTurnTiming = z.infer<typeof SdkTurnTimingSchema>;

/**
 * Sandbox-side diagnostics. The dedicated channel for telemetry only the
 * sandbox can measure — separate from the protocol events (`system_init` etc.)
 * so a reader of those never carries an observability concern. New metric
 * groups slot in beside `boot` (each optional: an event carries whatever it
 * measured this turn).
 *
 * That last clause is load-bearing rather than decorative: the groups become
 * known at different moments, so the runner emits one event per group as each
 * lands. `boot` goes out right after the first SDK message, `sdk` at turn end
 * where the result carries it. The host merges them per group — see the
 * facade's `runner_metrics` arm — so an event carrying one group never erases
 * another.
 *
 * `.strict()` is applied at the union site and wraps THIS envelope only, not
 * the group objects inside it. So the two directions fail differently, and only
 * one of them is loud:
 *
 *   - a new metric GROUP a host has not been told about refuses the whole event,
 *     which is the loud-failure contract the union is built on;
 *   - a new FIELD inside a group the host already knows is stripped in silence.
 *
 * Release order is load-bearing for both — the host learns a shape before a runner emits
 * it — and free here, because a new runner reaches a sandbox only through a
 * snapshot rebake, which follows the host
 * deploy. What the silent half costs is a reader who adds a figure to `sdk` and
 * finds it missing rather than refused: widen the schema first. Measured on the
 * seventh, by removing its member here while the runner kept stamping it — the
 * event parsed, the other members survived, and only that one was gone.
 */
export const RunnerMetricsSchema = AgentIpcEnvelopeBaseSchema.extend({
  kind: z.literal(AGENT_IPC_KIND.RunnerMetrics),
  boot: RunnerBootTimingSchema.optional(),
  sdk: SdkTurnTimingSchema.optional()
});

export type RunnerMetricsEvent = z.infer<typeof RunnerMetricsSchema>;

/** The host-facing payload — the event minus its wire envelope. Captured into a
 * facade getter and read at session transport finalisation, exactly like NativeResumeStatus. */
export type RunnerMetrics = Omit<RunnerMetricsEvent, "kind" | "protocolVersion">;
